import { useRef } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { Remote, type Injectables } from '@proposify/mfe-tools';
import { trackError } from '@proposify/frontend-observability';
import type { UserEntity } from 'services/entities/UserEntity';
import { NotificationsProvider } from 'components/notifications/NotificationsProvider';
import { I18nProvider } from 'providers/I18nProvider';
import PermissionsProvider from 'providers/PermissionsProvider';
import { QueryProvider } from 'providers/QueryProvider';
import { QueryProviderV5 } from 'providers/QueryProviderV5';
import { InjectableUserProvider } from 'providers/UserProvider';
import { ApiUserPermissionsRepository } from 'services/permissions/implementations/ApiUserPermissionsRepository';
import { EditorContentWrapper } from 'pages/editor/editorContentWrapper';
import { ContentType } from 'utils/getContentType';
import { designToken, DesignTokenProvider } from '@proposify/ui';

export interface SectionEditorInjectables extends Injectables {
  user: UserEntity;
  isFrontendUserTokenEnabled: boolean;
  contentId: string;
}

const REQUIRED_ENV_VARS = [
  'VITE_DOCUMENT_SERVICE_API_ENDPOINT',
  'VITE_DOCUMENT_SERVICE_API_HOST',
  'VITE_DOCUMENT_SERVICE_SOCKET_ENDPOINT',
] as const;

function assertRequiredEnvVars(): void {
  const missing = REQUIRED_ENV_VARS.filter((key) => !globalThis.env?.[key]);
  if (missing.length > 0) {
    trackError(new Error(`SectionEditorMicrofrontend: missing required env vars: ${missing.join(', ')}`));
  }
}

function validateInjectables(injectables: object): asserts injectables is SectionEditorInjectables {
  if ('user' in injectables && 'contentId' in injectables) {
    if (typeof injectables.contentId === 'string' && injectables.contentId !== '') {
      return;
    }
  }

  const error = new Error('validation failure, could not load SectionEditorMicrofrontend');
  trackError(error, {
    injectablesFromHost: injectables,
  });
  throw error;
}

const userPermissionsRepository = new ApiUserPermissionsRepository();

const SectionEditorMicrofrontend = () => {
  const envAssertedRef = useRef(false);
  if (!envAssertedRef.current) {
    envAssertedRef.current = true;
    assertRequiredEnvVars();
  }

  return (
    <Remote name="section-editor-remote" validateInjectables={validateInjectables}>
      {(injectables) => (
        // HelmetProvider is required by react-helmet-async and is added independently in each
        // MFE entrypoint, since this app has no single shared root the provider tree can live in.
        <HelmetProvider>
          <I18nProvider>
            <QueryProvider>
              <QueryProviderV5>
                <DesignTokenProvider theme={designToken}>
                  <InjectableUserProvider user={injectables.user}>
                    <PermissionsProvider repository={userPermissionsRepository}>
                      <NotificationsProvider>
                        <EditorContentWrapper contentId={injectables.contentId} contentType={ContentType.SECTION} />
                      </NotificationsProvider>
                    </PermissionsProvider>
                  </InjectableUserProvider>
                </DesignTokenProvider>
              </QueryProviderV5>
            </QueryProvider>
          </I18nProvider>
        </HelmetProvider>
      )}
    </Remote>
  );
};

const SectionEditorMicrofrontendEntry = () => <SectionEditorMicrofrontend />;
export default SectionEditorMicrofrontendEntry;
